import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Eye, Trash2, MoreVertical } from "lucide-react";
import { Button } from "../components/ui/button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";

const STATUS_VARIANT = { paid: "secondary", partial: "default", unpaid: "destructive", cancelled: "outline" };

export default function Invoices() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canManage = ["admin", "manager"].includes(user?.role);
  const [items, setItems] = useState([]);

  const load = () => api.get("/invoices").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const updateStatus = async (inv, status) => {
    try {
      await api.patch(`/invoices/${inv.id}/status?status=${status}`);
      toast.success(`Marked ${status}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (inv) => {
    if (!window.confirm(`Void invoice ${inv.invoice_no}?\n\nThis will:\n• Reverse FIFO stock back into the original batches\n• Reverse wallet earn/redeem rows\n• Permanently delete the invoice\n\nProceed?`)) return;
    try {
      const { data } = await api.delete(`/invoices/${inv.id}`);
      toast.success(`Voided ${data.invoice_no} — ${data.restored_units} units restored, ₹${data.wallet_reversed} wallet reversed`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-5" data-testid="invoices-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Billing</div>
        <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">Invoices</h1>
      </div>
      <Card className="p-4">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Invoice</TableHead>
              <TableHead>When</TableHead>
              <TableHead>Billed by</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map(i => (
              <TableRow key={i.id} data-testid={`invoice-row-${i.id}`}>
                <TableCell className="font-mono font-semibold">{i.invoice_no}</TableCell>
                <TableCell className="text-sm">{new Date(i.created_at).toLocaleString()}</TableCell>
                <TableCell className="text-sm">{i.billed_by_name}</TableCell>
                <TableCell className="text-right font-mono">₹{i.grand_total.toFixed(2)}</TableCell>
                <TableCell><Badge variant={STATUS_VARIANT[i.status] || "outline"}>{i.status}</Badge></TableCell>
                <TableCell className="text-right">
                  <Button asChild variant="ghost" size="sm"><Link to={`/invoices/${i.id}`} data-testid={`invoice-view-${i.id}`}><Eye className="w-4 h-4 mr-1" />Open</Link></Button>
                  {canManage && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" data-testid={`invoice-menu-${i.id}`}><MoreVertical className="w-4 h-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => updateStatus(i, "paid")} disabled={i.status === "paid"} data-testid={`invoice-mark-paid-${i.id}`}>Mark paid</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => updateStatus(i, "partial")} disabled={i.status === "partial"}>Mark partial</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => updateStatus(i, "unpaid")} disabled={i.status === "unpaid"}>Mark unpaid</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => updateStatus(i, "cancelled")} disabled={i.status === "cancelled"}>Mark cancelled</DropdownMenuItem>
                        {isAdmin && <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => del(i)} className="text-destructive focus:text-destructive" data-testid={`invoice-void-${i.id}`}>
                            <Trash2 className="w-4 h-4 mr-2" />Void invoice (restore stock)
                          </DropdownMenuItem>
                        </>}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No invoices yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
